import { z } from 'zod';
import { useMemo } from 'react';
import { TMDXFile } from '@/api/apis/files';
import { STATE_SCHEMA } from '@/utils/helpers';
import { NewTXCenterForm } from './types';
import { TX_USER_FORM_DEFAULT_VALUES, txUserSchema } from '../TXUserFormBase';
import { createTxCenterFormValidation } from './utils';
import { CustomerPermissionEnum } from '@/api/apis/customer';
import { MAX_LENGTH_2000 } from '@/constants/form.constants';
import { NameByValue } from '@/types';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

export const DEFAULT_TX_CENTER_VALUES: NewTXCenterForm = {
  name: '',
  code: '',
  timezone: '',
  timezoneIANA: '',
  addressId: '',
  addressLine: '',
  city: '',
  state: null,
  postalCode: '',
  country: '',
  notes: '',
  hasFixedPriceContract: false,
  longitude: 0,
  latitude: 0,
  trialProgramIds: [],
  trialProgramNotes: '',
  qadCustomerId: null,
  ...TX_USER_FORM_DEFAULT_VALUES,
};

export const txCenterSchema = z.object({
  name: z
    .string()
    .min(1, 'Provide tx center name')
    .refine((value) => !/^\s*$/.test(value), 'Provide tx center name'),
  code: z.string().trim().min(1, 'Provide tx center code'),
  image: z.custom<TMDXFile>().optional(),
  logoPath: z.string().optional(),
  timezone: z.string(),
  timezoneIANA: z.string(),
  addressId: z.string().optional(),
  addressLine: z.string().min(1, 'Provide tx center address'),
  city: z.string().min(1, 'Provide tx center city'),
  state: STATE_SCHEMA,
  postalCode: z.string().min(1, 'Provide tx center postal code'),
  country: z.string(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  primaryTXUserId: z.string().optional(),
  notes: z.string().max(MAX_LENGTH_2000, `Maximum length: up to ${MAX_LENGTH_2000} symbols`).optional(),
  hasFixedPriceContract: z.boolean(),
  trialProgramIds: z.string().array().optional(),
  trialProgramNotes: z.string().max(MAX_LENGTH_2000, `Maximum length: up to ${MAX_LENGTH_2000} symbols`).optional(),
  qadCustomerId: z
    .string()
    .regex(/^\d*$/, 'Only numeric characters (0–9), max length up to 10 characters')
    .max(10, 'Only numeric characters (0–9), max length up to 10 characters')
    .nullable()
    .optional(),
});

export const txCenterFormSchema = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  txCenterSchema
    .extend(
      txUserSchema.omit({ txCenterId: true, txCenterName: true }).extend({
        permissions: z.enum(CustomerPermissionEnum).array().default([]),
      }).shape,
    )
    .superRefine(createTxCenterFormValidation(composedSubdivisionsList));

export const useTxCenterFormSchema = () => {
  const { composedSubdivisionsList, isLoading } = useCountriesData();
  const schema = useMemo(() => txCenterFormSchema(composedSubdivisionsList), [composedSubdivisionsList]);
  return { schema, isLoading };
};
